import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Box, Divider, Drawer, IconButton } from '@mui/material';
import { getIconFromGoogleMimeType } from '../../utils/company-utils';
import CloseIcon from '@mui/icons-material/Close';
import Label from '../common/Label';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material/styles';
import {
	Template,
	getReadableGoogleMimeType,
} from '../../data/models/template';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Tabs from '../common/Tabs';
import ScreenBusySpinner from '../common/ScreenBusySpinner';
import Paper, { PaperHeader, PaperHeaderNav } from '../common/layout/Paper';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import { fetchConfigurationThunk } from '../../data/thunks/projectConfigurationThunk';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import ProjectTypeTemplates from './ProjectTypeTemplates';
import ProjectTypeFolders from './ProjectTypeFolders';
import ProjectTypeData from './ProjectTypeData';

export default function ConfigurationDetailsScreen() {
	const theme = useTheme();
	const { configurationId } = useParams();
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	const [currentTab, setCurrentTab] = useState('templates');

	const { configuration } = useSelector(
		(state: RootState) => state.projectConfigurationState
	);

	useEffect(() => {
		if (configurationId) {
			dispatch(fetchConfigurationThunk(configurationId));
		}
	}, [configurationId]);

	const panels = [
		{
			label: 'Templates',
			component: <ProjectTypeTemplates />,
			key: 'templates',
			onclick: () => setCurrentTab('templates'),
		},
		{
			label: 'Folders',
			component: <ProjectTypeFolders />,
			key: 'folders',
			onclick: () => setCurrentTab('folders'),
		},
		{
			label: 'Data',
			component: <ProjectTypeData />,
			key: 'data',
			onclick: () => setCurrentTab('data'),
		},
	];

	if (configuration.id !== configurationId) {
		return <ScreenBusySpinner />;
	}

	return (
		<>
			<ScreenBusySpinner />
			<PageWrapper>
				<Container gradientBorder>
					<Box
						sx={{
							width: '100%',
							backgroundColor: theme.palette.background.paper,
							borderRadius: 2,
							position: 'relative',
							flex: 1,
							overflow: 'hidden',
							height: 'calc(100vh - 98px)',
							padding: 1,
						}}
					>
						<PaperHeader>
							<PaperHeaderNav
								onClick={() => navigate('/project-types')}
							>
								Project Types <ChevronRightOutlinedIcon />
							</PaperHeaderNav>
							<ConfigurationName>
								{configuration.name}
							</ConfigurationName>
						</PaperHeader>
						<Tabs panels={panels} currentPath={currentTab} />
					</Box>
				</Container>
			</PageWrapper>
		</>
	);
}

const PageWrapper = styled.div`
	display: flex;
	width: 100%;
	position: relative;
`;

const ConfigurationName = styled.div`
	display: inline;
	margin-right: 20px;
	color: ${({ theme }) => theme.palette.text.primary};
`;

const Container = styled(Paper)<{ $drawerOpen?: boolean }>`
	flex: 1;
	transition: width 0.3s ease-out;
	width: ${(props) => (props.$drawerOpen ? 'calc(100% - 360px)' : '100%')};
	margin-right: ${(props) => (props.$drawerOpen ? '348px' : '0')};
`;

const DescriptionText = styled.div`
	color: ${(props) => props.theme.palette.text.secondary};
`;

const TemplateName = styled.div`
	font-size: 16px;
	max-width: 80%;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const FileDetails = styled.div`
	font-size: 16px;
	font-weight: 500;
`;

const StyledDivider = styled(Divider)`
	margin: 16px -16px !important;
`;

const StyledCloseButton = styled(IconButton)`
	position: absolute !important;
	top: 8px;
	right: 8px;
`;

const DrawerContainer = styled(Paper)`
	background-color: ${(props) => props.theme.palette.background.paper};
	padding: 6px;
	width: 335px;
	margin-top: 80px;
	border-radius: 8px;
	position: relative;
	font-size: 14px;
`;

const DetailsContainer = styled.div`
	margin-top: 16px;
`;

const DetailItem = styled.div`
	margin-bottom: 8px;
`;

const DetailValue = styled.div`
	margin-left: 8px;
	color: ${(props) => props.theme.palette.text.secondary};
`;

const NameIconContainer = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 16px;
`;
